import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { apiFetch } from '../api';
import {
  Entitlement,
  ModuleKey,
  PERMISSIVE_ENTITLEMENT,
  hasBillingBanner,
  isModuleEnabled,
  parseEntitlementFromReadiness,
} from './entitlement';

/**
 * Global entitlement provider (plan §8).
 *
 * Reads `/api/settings/readiness` on mount + every `REFRESH_INTERVAL_MS`
 * so changes pushed by the admin (tariff override, billing_state flip)
 * are picked up without a page reload. On fetch failure we keep the
 * last-known envelope rather than falling back to the permissive
 * default — prevents a flicker from "AI gated" → "AI unlocked" → "AI
 * gated" when a retried request eventually succeeds.
 */

const REFRESH_INTERVAL_MS = 60_000;

interface EntitlementContextValue {
  entitlement: Entitlement;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  isModuleEnabled: (module: ModuleKey) => boolean;
  bannerKind: ReturnType<typeof hasBillingBanner>;
}

const EntitlementContext = createContext<EntitlementContextValue | null>(null);

interface EntitlementProviderProps {
  children: React.ReactNode;
  /**
   * Skip the initial fetch. Used for the login page where we have no
   * token yet — the provider still renders so downstream components
   * can call `useEntitlement()` without guarding.
   */
  disabled?: boolean;
}

export function EntitlementProvider({ children, disabled = false }: EntitlementProviderProps) {
  const [entitlement, setEntitlement] = useState<Entitlement>(PERMISSIVE_ENTITLEMENT);
  const [loading, setLoading] = useState<boolean>(!disabled);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef<boolean>(true);

  const fetchReadiness = useCallback(async (): Promise<void> => {
    try {
      const response = await apiFetch('/api/settings/readiness');
      if (!response.ok) {
        // 403 = user lacks readiness permission but they can still use
        // the app. Do NOT clobber state with a permissive fallback —
        // just stop trying.
        if (response.status === 403) {
          if (mountedRef.current) setLoading(false);
          return;
        }
        throw new Error(`readiness http ${response.status}`);
      }
      const body: unknown = await response.json();
      const next = parseEntitlementFromReadiness(body);
      if (mountedRef.current) {
        setEntitlement(next);
        setError(null);
        setLoading(false);
      }
    } catch (err: unknown) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'unknown error');
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (disabled) {
      setLoading(false);
      return () => {
        mountedRef.current = false;
      };
    }
    void fetchReadiness();
    const timer = window.setInterval(() => {
      void fetchReadiness();
    }, REFRESH_INTERVAL_MS);
    // Also refresh when the tab regains focus — the user may have just
    // come back from the /billing page where they upgraded.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void fetchReadiness();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      mountedRef.current = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [disabled, fetchReadiness]);

  const moduleCheck = useCallback(
    (module: ModuleKey) => isModuleEnabled(entitlement, module),
    [entitlement],
  );

  const value = useMemo<EntitlementContextValue>(
    () => ({
      entitlement,
      loading,
      error,
      refresh: fetchReadiness,
      isModuleEnabled: moduleCheck,
      bannerKind: hasBillingBanner(entitlement),
    }),
    [entitlement, loading, error, fetchReadiness, moduleCheck],
  );

  return (
    <EntitlementContext.Provider value={value}>{children}</EntitlementContext.Provider>
  );
}

/**
 * Hook that returns the full entitlement envelope + helpers.
 *
 * Falls back to the permissive default when called outside a provider
 * (e.g. in a Storybook story or a unit test). This matches the backend
 * contract: when anything is unclear, fail-open.
 */
export function useEntitlement(): EntitlementContextValue {
  const ctx = useContext(EntitlementContext);
  if (ctx) return ctx;
  return {
    entitlement: PERMISSIVE_ENTITLEMENT,
    loading: false,
    error: null,
    refresh: async () => undefined,
    isModuleEnabled: (module: ModuleKey) =>
      isModuleEnabled(PERMISSIVE_ENTITLEMENT, module),
    bannerKind: null,
  };
}

/**
 * Convenience hook — returns true if the given module is currently
 * enabled for this shop. Components use this to render gated UI
 * inline without pulling the full envelope.
 */
export function useModule(module: ModuleKey): boolean {
  const { isModuleEnabled: check } = useEntitlement();
  return check(module);
}
